import React, { useMemo, useRef, useState } from 'react';
import { useForm, Controller, ControllerRenderProps, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  CircularProgress,
  Link,
  Checkbox,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCountryOptions } from '../translations/getCountryOptions';
import { validateEmail } from '../validations/validations';
import { registerSimpleForm } from '../services/simple-form';
import { ValidateSpanishID, SPANISH_ID_TYPES } from '../validations/nifValidation';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { palette } from '../constants/palette';

type FormValues = {
  country: string;
  userType: 'Person' | 'Company';
  mailAddress: string;
  companyName: string;
  firstName: string;
  lastName: string;
  nationalId: string;
  vatId: string;
};

const FIELD_MAX = {
  country: 255,
  mailAddress: 255,
  companyName: 255,
  firstName: 255,
  lastName: 255,
  nationalId: 200,
  vatId: 255,
} as const;

const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

const isSpain = (country?: string) => country === 'ES';
const isEuOutsideSpain = (country?: string) =>
  !!country && country !== 'ES' && EU_COUNTRIES.includes(country);
const isOutsideEu = (country?: string) => !!country && !EU_COUNTRIES.includes(country);
const normalizeNationalId = (value: string) => value.replace(/\s+/g, '').toUpperCase();
const SimpleForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const baseSchema = useMemo(
    () =>
      yup.object({
        country: yup
          .string()
          .required(t('simpleForm.validation.countryRequired'))
          .max(
            FIELD_MAX.country,
            t('registration.validation.maxLength', { max: FIELD_MAX.country }),
          ),

        userType: yup
          .mixed<'Person' | 'Company'>()
          .oneOf(['Person', 'Company'])
          .required(t('simpleForm.validation.userTypeRequired')),

        mailAddress: yup
          .string()
          .required(t('simpleForm.validation.emailRequired'))
          .max(
            FIELD_MAX.mailAddress,
            t('registration.validation.maxLength', { max: FIELD_MAX.mailAddress }),
          )
          .test('email-format', t('simpleForm.validation.invalidEmail'), (value) =>
            value ? validateEmail(value) : false,
          ),

        companyName: yup.string().when('userType', {
          is: 'Company',
          then: (schema) =>
            schema
              .required(t('simpleForm.validation.companyNameRequired'))
              .max(
                FIELD_MAX.companyName,
                t('registration.validation.maxLength', { max: FIELD_MAX.companyName }),
              ),
          otherwise: (schema) => schema.notRequired(),
        }),

        firstName: yup.string().when('userType', {
          is: 'Person',
          then: (schema) =>
            schema
              .required(t('simpleForm.validation.firstNameRequired'))
              .max(
                FIELD_MAX.firstName,
                t('registration.validation.maxLength', { max: FIELD_MAX.firstName }),
              ),
          otherwise: (schema) => schema.notRequired(),
        }),

        lastName: yup.string().when('userType', {
          is: 'Person',
          then: (schema) =>
            schema
              .required(t('simpleForm.validation.lastNameRequired'))
              .max(
                FIELD_MAX.lastName,
                t('registration.validation.maxLength', { max: FIELD_MAX.lastName }),
              ),
          otherwise: (schema) => schema.notRequired(),
        }),

        nationalId: yup.string().when('country', ([country], schema) => {
          let s = schema.max(
            FIELD_MAX.nationalId,
            t('registration.validation.maxLength', { max: FIELD_MAX.nationalId }),
          );

          if (isSpain(country)) {
            return s
              .required(t('simpleForm.validation.nationalIdRequiredSpain'))
              .test('spanish-id', t('simpleForm.validation.invalidDocument'), function (value) {
                const { userType } = this.parent as { userType: 'Person' | 'Company' };

                if (!value) return false;

                const normalizedValue = normalizeNationalId(value);
                const { valid, type } = ValidateSpanishID(normalizedValue);

                if (!valid) {
                  return this.createError({
                    message: t('simpleForm.validation.invalidDocument'),
                  });
                }

                if (userType === 'Company' && type !== SPANISH_ID_TYPES.CIF) {
                  return this.createError({
                    message: t('simpleForm.validation.invalidCif'),
                  });
                }

                if (
                  userType === 'Person' &&
                  type !== SPANISH_ID_TYPES.DNI &&
                  type !== SPANISH_ID_TYPES.NIE
                ) {
                  return this.createError({
                    message: t('simpleForm.validation.invalidNifNie'),
                  });
                }

                return true;
              });
          }

          if (isEuOutsideSpain(country)) {
            return s.required(t('simpleForm.validation.taxIdRequired'));
          }

          return s.notRequired();
        }),

        vatId: yup
          .string()
          .max(FIELD_MAX.vatId, t('registration.validation.maxLength', { max: FIELD_MAX.vatId })),
      }) as yup.ObjectSchema<FormValues>,
    [t],
  );

  const resolver = useMemo(() => yupResolver(baseSchema), [baseSchema]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver,
    defaultValues: {
      country: 'ES',
      userType: 'Person',
      mailAddress: '',
      companyName: '',
      firstName: '',
      lastName: '',
      nationalId: '',
      vatId: '',
    },
    mode: 'onChange',
  });

  const country = useWatch({ control, name: 'country' });
  const userType = useWatch({ control, name: 'userType' });

  const onCaptchaChange = (token: string | null) => {
    setIsCaptchaVerified(!!token);
    setCaptchaToken(token || '');
  };

  const getNationalIdLabel = () => {
    if (isSpain(country)) {
      return userType === 'Company' ? t('simpleForm.fields.cif') : t('simpleForm.fields.nifNie');
    }

    if (isEuOutsideSpain(country)) {
      return t('simpleForm.fields.taxId');
    }

    return t('simpleForm.fields.taxIdOptional');
  };

  const getHelperText = () => {
    if (isSpain(country)) {
      return userType === 'Company'
        ? t('simpleForm.helpers.companyCif')
        : t('simpleForm.helpers.personNifNie');
    }

    if (isEuOutsideSpain(country)) {
      return t('simpleForm.helpers.euRequired');
    }

    if (isOutsideEu(country)) {
      return t('simpleForm.helpers.outsideEuOptional');
    }

    return '';
  };

  const onSubmit = async (data: FormValues) => {
    if (!isCaptchaVerified || !termsAccepted) return;

    setIsSubmitting(true);
    setErrorMessage('');
    setErrorModalOpen(false);

    try {
      const payload = {
        country: data.country,
        userType: data.userType,
        mailAddress: data.mailAddress.trim(),
        companyName:
          data.userType === 'Person'
            ? `${data.firstName.trim()} ${data.lastName.trim()}`.trim()
            : data.companyName.trim(),
        firstName: data.userType === 'Person' ? data.firstName.trim() : '',
        lastName: data.userType === 'Person' ? data.lastName.trim() : '',
        nationalId: normalizeNationalId(data.nationalId || ''),
        vatId: data.vatId?.trim() || '',
      };

      const response = await registerSimpleForm(payload, captchaToken);

      navigate('/simple-form/success', {
        state: {
          companyId: response.companyId,
          companyName: response.companyName || payload.companyName,
          mailAddress: payload.mailAddress,
          temporaryPassword: response.temporaryPassword,
        },
      });
    } catch (error: unknown) {
      if (typeof error === 'object' && error && 'message' in error) {
        setErrorMessage(
          (error as { message?: string }).message || t('simpleForm.errors.unknownError'),
        );
      } else {
        setErrorMessage(t('simpleForm.errors.submitError'));
      }

      setErrorModalOpen(true);
      recaptchaRef.current?.reset();
      setIsCaptchaVerified(false);
      setCaptchaToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const countryOptions = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <LanguageSwitcher />
      <Paper
        elevation={4}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          border: `1px solid ${palette.primaryLighter}`,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', sm: '2rem' }, fontWeight: 700 }}>
            {t('simpleForm.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('simpleForm.subtitle')}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12}>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={countryOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={countryOptions.find((c) => c.code === field.value) || null}
                    onChange={(_, newValue) => field.onChange(newValue ? newValue.code : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('simpleForm.fields.country')}
                        fullWidth
                        margin="dense"
                        error={!!errors.country}
                        helperText={errors.country?.message}
                        inputProps={{
                          ...params.inputProps,
                          maxLength: FIELD_MAX.country,
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="userType"
                control={control}
                render={({ field }: { field: ControllerRenderProps<FormValues, 'userType'> }) => (
                  <FormControl component="fieldset" error={!!errors.userType}>
                    <FormLabel component="legend">{t('simpleForm.fields.userType')}</FormLabel>
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value="Person"
                        control={<Radio />}
                        label={t('simpleForm.userTypes.person')}
                      />
                      <FormControlLabel
                        value="Company"
                        control={<Radio />}
                        label={t('simpleForm.userTypes.company')}
                      />
                    </RadioGroup>
                    {errors.userType && <FormHelperText>{errors.userType.message}</FormHelperText>}
                  </FormControl>
                )}
              />
            </Grid>

            {userType === 'Person' && (
              <>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'firstName'>;
                    }) => (
                      <TextField
                        {...field}
                        label={t('simpleForm.fields.firstName')}
                        fullWidth
                        margin="dense"
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        inputProps={{ maxLength: FIELD_MAX.firstName }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<FormValues, 'lastName'>;
                    }) => (
                      <TextField
                        {...field}
                        label={t('simpleForm.fields.lastName')}
                        fullWidth
                        margin="dense"
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        inputProps={{ maxLength: FIELD_MAX.lastName }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {userType === 'Company' && (
              <Grid item xs={12}>
                <Controller
                  name="companyName"
                  control={control}
                  render={({
                    field,
                  }: {
                    field: ControllerRenderProps<FormValues, 'companyName'>;
                  }) => (
                    <TextField
                      {...field}
                      label={t('simpleForm.fields.companyName')}
                      fullWidth
                      margin="dense"
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                      inputProps={{ maxLength: FIELD_MAX.companyName }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                name="mailAddress"
                control={control}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormValues, 'mailAddress'>;
                }) => (
                  <TextField
                    {...field}
                    label={t('simpleForm.fields.email')}
                    type="email"
                    fullWidth
                    margin="dense"
                    error={!!errors.mailAddress}
                    helperText={errors.mailAddress?.message}
                    inputProps={{ maxLength: FIELD_MAX.mailAddress }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="nationalId"
                control={control}
                render={({ field }: { field: ControllerRenderProps<FormValues, 'nationalId'> }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    onChange={(event) => {
                      field.onChange(normalizeNationalId(event.target.value));
                    }}
                    label={getNationalIdLabel()}
                    fullWidth
                    margin="dense"
                    error={!!errors.nationalId}
                    helperText={errors.nationalId?.message || getHelperText()}
                    inputProps={{ maxLength: FIELD_MAX.nationalId }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="vatId"
                control={control}
                render={({ field }: { field: ControllerRenderProps<FormValues, 'vatId'> }) => (
                  <TextField
                    {...field}
                    label={t('simpleForm.fields.vatId')}
                    fullWidth
                    margin="dense"
                    error={!!errors.vatId}
                    helperText={errors.vatId?.message || t('simpleForm.optional')}
                    inputProps={{ maxLength: FIELD_MAX.vatId }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  '& > div': {
                    '@media (max-width: 340px)': {
                      transform: 'scale(0.85)',
                      transformOrigin: 'center',
                    },
                  },
                }}
              >
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
                  onChange={onCaptchaChange}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted((e.target as HTMLInputElement).checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('simpleForm.terms.acceptPrefix')}{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsTermsModalOpen(true);
                      }}
                      sx={{ textDecoration: 'underline' }}
                    >
                      {t('simpleForm.terms.linkText')}
                    </Link>
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={!isCaptchaVerified || !termsAccepted || isSubmitting}
                sx={{ mt: 1, py: 1.3 }}
              >
                {isSubmitting
                  ? t('simpleForm.actions.registering')
                  : t('simpleForm.actions.register')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Dialog
          open={isTermsModalOpen}
          onClose={() => setIsTermsModalOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>{t('simpleForm.terms.title')}</DialogTitle>
          <DialogContent sx={!isMobile ? { height: '400px' } : {}}>
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.general.title')}
            </Typography>
            {t('simpleForm.terms.sections.general.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.definitions.title')}
            </Typography>
            {t('simpleForm.terms.sections.definitions.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.scope.title')}
            </Typography>
            {t('simpleForm.terms.sections.scope.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.access.title')}
            </Typography>
            {t('simpleForm.terms.sections.access.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.usage.title')}
            </Typography>
            {t('simpleForm.terms.sections.usage.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.privacy.title')}
            </Typography>
            {t('simpleForm.terms.sections.privacy.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.communications.title')}
            </Typography>
            {t('simpleForm.terms.sections.communications.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.intellectualProperty.title')}
            </Typography>
            {t('simpleForm.terms.sections.intellectualProperty.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.liability.title')}
            </Typography>
            {t('simpleForm.terms.sections.liability.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.thirdPartyLinks.title')}
            </Typography>
            {t('simpleForm.terms.sections.thirdPartyLinks.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.modifications.title')}
            </Typography>
            {t('simpleForm.terms.sections.modifications.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.termination.title')}
            </Typography>
            {t('simpleForm.terms.sections.termination.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.jurisdiction.title')}
            </Typography>
            {t('simpleForm.terms.sections.jurisdiction.body')}
            <br />
            <br />
            <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
              {t('simpleForm.terms.sections.contact.title')}
            </Typography>
            {t('simpleForm.terms.sections.contact.body')}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsTermsModalOpen(false)}>
              {t('simpleForm.actions.close')}
            </Button>
            <Button
              onClick={() => {
                setTermsAccepted(true);
                setIsTermsModalOpen(false);
              }}
              variant="contained"
            >
              {t('simpleForm.actions.accept')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isSubmitting} PaperProps={{ sx: { textAlign: 'center', p: 4 } }}>
          <DialogContent>
            <CircularProgress />
            <DialogContentText sx={{ mt: 2 }}>{t('simpleForm.status.sending')}</DialogContentText>
          </DialogContent>
        </Dialog>

        <Dialog open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
          <DialogTitle>{t('simpleForm.errors.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{errorMessage}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setErrorModalOpen(false)} autoFocus>
              {t('simpleForm.actions.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default SimpleForm;
